import { useCallback, useEffect, useRef, useState } from "react";

import AgoraRTC, {
  createCameraVideoTrack,
  createClient,
  createMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import {
  QUALITY_PRESETS,
  AUTO_LADDER,
  VIDEO_CODEC,
  PERSISTABLE_MODES,
  LOW_STREAM_PARAMS,
  STREAM_FALLBACK_LOW_STREAM,
  AUDIO_ONLY_FLOOR_ENABLED,
  AUDIO_ENCODER_DEFAULT,
  AUDIO_ENCODER_DATA_SAVER,
} from "../../../components/consultation/configs/qualityPresets";

import { switchCamera as switchAgoraCamera } from "./cameraUtils";

const HARD_END_PADDING_MS = 250;

const INITIAL_NETWORK_QUALITY = Object.freeze({
  status: "CHECKING",
  uplink: null,
  downlink: null,
  poorPersistent: false,
});

const POOR_NETWORK_SAMPLE_LIMIT = 3;

/*
 * Auto-ladder tuning. Agora emits "network-quality" about every 2 s.
 *
 * Step down after sustained poor uplink, step up slowly, and wait between
 * steps so the new preset can take effect before it is judged.
 */
const STEP_DOWN_QUALITY = 4; // uplink quality >= 4 counts as poor
const STEP_DOWN_SAMPLES = 5; // ~10 s of poor uplink
const STEP_UP_SAMPLES = 20; // ~40 s of good uplink
const AUDIO_ONLY_SAMPLES = 15; // ~30 s at the floor (only if enabled)
const STEP_COOLDOWN_MS = 10000;

const RECONNECT_PHONE_FALLBACK_MS = 60000;

const PUBLISH_MAX_ATTEMPTS = 3;
const PUBLISH_RETRY_BASE_MS = 1000;

/* Late camera recovery after an audio-only join. */
const VIDEO_RECOVERY_DELAYS_MS = [5000, 15000, 30000];

const MANUAL_MODE_STORAGE_KEY = "deedmed.video.manualMode";
const RTC_DEBUG_STORAGE_KEY = "deedmed.debugRtc";

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/* ---------- optional field diagnostics ----------
 * Enable in the browser console:
 *   localStorage.setItem("deedmed.debugRtc", "1")   (then reload)
 * Disable: localStorage.removeItem("deedmed.debugRtc")
 */
const isRtcDebugEnabled = () => {
  try {
    return window.localStorage.getItem(RTC_DEBUG_STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
};

/* ---------- persisted manual mode (per device) ---------- */

const readStoredManualMode = () => {
  try {
    const stored = window.localStorage.getItem(MANUAL_MODE_STORAGE_KEY);
    if (
      stored &&
      PERSISTABLE_MODES.includes(stored) &&
      QUALITY_PRESETS[stored]
    ) {
      return stored;
    }
  } catch (_) {
    /* storage may be unavailable; ignore */
  }
  return null;
};

const writeStoredManualMode = (mode) => {
  try {
    if (PERSISTABLE_MODES.includes(mode)) {
      window.localStorage.setItem(MANUAL_MODE_STORAGE_KEY, mode);
    } else if (mode === "auto") {
      window.localStorage.removeItem(MANUAL_MODE_STORAGE_KEY);
    }
    /* detail / motion / stable are temporary: never saved */
  } catch (_) {
    /* ignore */
  }
};

/*
 * Lightweight pre-flight hint (Chrome/Android only; absent elsewhere).
 * A slow or "save data" connection starts in data-save instead of waiting
 * for the ladder to discover it.
 */
const looksLikeWeakConnection = () => {
  try {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) return false;
    if (connection.saveData) return true;

    return ["slow-2g", "2g", "3g"].includes(connection.effectiveType);
  } catch (_) {
    return false;
  }
};

const resolveInitialMode = () => {
  const stored = readStoredManualMode();

  if (stored) {
    return { mode: stored, source: "manual" };
  }

  if (looksLikeWeakConnection()) {
    return { mode: "data-save", source: "auto" };
  }

  return { mode: "auto", source: "auto" };
};

/* ---------- error helpers ---------- */

const isPermissionError = (error) => {
  const errorName = String(error?.name || "").toLowerCase();
  const errorMessage = String(error?.message || "").toLowerCase();

  return (
    errorName.includes("notallowed") ||
    errorName.includes("permission") ||
    errorMessage.includes("permission denied") ||
    errorMessage.includes("notallowed")
  );
};

/*
 * Works with both native DOMException names and AgoraRTCError codes.
 * "unknown" is treated like a constraint failure by the camera ladder
 * (step down).
 */
const getMediaErrorKind = (error) => {
  const name = String(error?.name || "").toLowerCase();
  const code = String(error?.code || "").toUpperCase();
  const message = String(error?.message || "").toLowerCase();

  if (isPermissionError(error) || code === "PERMISSION_DENIED") {
    return "permission";
  }

  if (
    code === "DEVICE_NOT_FOUND" ||
    name.includes("notfound") ||
    message.includes("device not found")
  ) {
    return "not-found";
  }

  if (code === "NOT_READABLE" || name.includes("notreadable")) {
    return "busy";
  }

  if (
    code === "CONSTRAINT_NOT_SATISFIED" ||
    name.includes("overconstrained") ||
    message.includes("constraint")
  ) {
    return "constraint";
  }

  return "unknown";
};

const stopAndCloseTrack = (track) => {
  if (!track) {
    return;
  }

  try {
    track.stop();
  } catch (error) {
    console.error("Unable to stop Agora track", error);
  }

  try {
    track.close();
  } catch (error) {
    console.error("Unable to close Agora track", error);
  }
};

/*
 * Camera preset fallback ladder (best -> most compatible).
 *
 * Only rungs LOWER than the requested preset are ever tried, so a failure
 * at 720p never jumps up to 1080p.
 *
 * The final rung (640x480 @ 24fps) matches the legacy preset that was
 * proven to work on the widest range of devices.
 */
const CAMERA_PRESET_LADDER = [
  { width: 1920, height: 1080, frameRate: 30 },
  { width: 1280, height: 720, frameRate: 30 },
  { width: 1280, height: 720, frameRate: 24 },
  { width: 960, height: 540, frameRate: 24 },
  { width: 640, height: 480, frameRate: 24 },
];

const isLowerThan = (rung, preferred) => {
  const rungArea = rung.width * rung.height;
  const preferredArea = preferred.width * preferred.height;

  if (rungArea !== preferredArea) {
    return rungArea < preferredArea;
  }

  return rung.frameRate < preferred.frameRate;
};

/* Scale the bitrate cap with resolution so a 540p rung never inherits 4000. */
const scaledBitrateMax = (preferred, rung) => {
  if (!preferred.bitrateMax) {
    return undefined;
  }

  const ratio =
    (rung.width * rung.height) / (preferred.width * preferred.height);

  return Math.max(150, Math.round(preferred.bitrateMax * ratio));
};

const buildCameraAttempts = (preferred) => {
  const attempts = [preferred];

  CAMERA_PRESET_LADDER.forEach((rung) => {
    if (!isLowerThan(rung, preferred)) {
      return;
    }

    const same = attempts.some(
      (a) =>
        a.width === rung.width &&
        a.height === rung.height &&
        a.frameRate === rung.frameRate,
    );

    if (!same) {
      const bitrateMax = scaledBitrateMax(preferred, rung);

      attempts.push(bitrateMax ? { ...rung, bitrateMax } : rung);
    }
  });

  return attempts;
};

const buildCameraOptions = (mode) => {
  const options = { encoderConfig: QUALITY_PRESETS[mode] };

  if (mode === "motion" || mode === "detail") {
    options.optimizationMode = mode;
  }

  return options;
};

/*
 * Error handling per kind:
 *   permission  -> stop immediately (no retry, avoids re-prompting)
 *   not-found   -> stop immediately (caller continues audio-only)
 *   busy        -> retry the SAME preset once, then stop
 *                  (a lower resolution will not free a busy camera)
 *   constraint / unknown -> step down to the next rung
 */
async function createVideoTrackWithFallback(cameraBaseOptions, isStillActive) {
  const preferred = cameraBaseOptions.encoderConfig;

  if (!preferred) {
    return {
      track: null,
      preset: null,
      error: new Error("Missing encoderConfig for camera"),
      aborted: false,
    };
  }

  const attempts = buildCameraAttempts(preferred);

  let lastError = null;
  let busyRetried = false;

  for (let i = 0; i < attempts.length; i += 1) {
    const attempt = attempts[i];

    try {
      const track = await createCameraVideoTrack({
        ...cameraBaseOptions,
        encoderConfig: attempt,
      });

      if (!isStillActive()) {
        stopAndCloseTrack(track);
        return { track: null, preset: null, error: null, aborted: true };
      }

      if (i > 0) {
        console.warn(
          "Camera preset downgraded to",
          attempt,
          "after earlier attempts failed",
        );
      }

      return { track, preset: attempt, error: null, aborted: false };
    } catch (err) {
      lastError = err;
      console.warn("Camera preset failed:", attempt, err?.message || err?.name);

      const kind = getMediaErrorKind(err);

      if (kind === "permission" || kind === "not-found") {
        break;
      }

      if (kind === "busy") {
        if (busyRetried) {
          break;
        }

        busyRetried = true;
        await sleep(600);

        if (!isStillActive()) {
          return { track: null, preset: null, error: null, aborted: true };
        }

        i -= 1; // retry the same preset once
      }
    }
  }

  return { track: null, preset: null, error: lastError, aborted: false };
}

async function createMicrophoneTrackWithConfig(mode) {
  const encoderConfig =
    mode === "data-save" ? AUDIO_ENCODER_DATA_SAVER : AUDIO_ENCODER_DEFAULT;

  try {
    return await createMicrophoneAudioTrack({
      encoderConfig,
      AEC: true,
      ANS: true,
      AGC: true,
    });
  } catch (error) {
    if (getMediaErrorKind(error) === "permission") {
      throw error;
    }

    console.warn("Microphone config rejected; using SDK defaults", error);
    return createMicrophoneAudioTrack();
  }
}

const resolveNetworkQuality = (stats) => {
  const uplink = Number(stats?.uplinkNetworkQuality);
  const downlink = Number(stats?.downlinkNetworkQuality);

  const validValues = [uplink, downlink].filter(
    (value) => Number.isInteger(value) && value >= 0 && value <= 6,
  );

  if (!validValues.length) {
    return {
      status: "CHECKING",
      uplink: null,
      downlink: null,
    };
  }

  /*
   * We deliberately use the worse side for the UI indicator.
   *
   * Good download + poor upload is still
   * a poor consultation experience.
   */
  const worstQuality = Math.max(...validValues);

  /*
   * Agora Web SDK quality scale:
   *
   * 0      unknown
   * 1      excellent
   * 2      good
   * 3      slightly impaired
   * 4      not smooth
   * 5      barely usable
   * 6      disconnected
   *
   * Product presentation:
   *
   * 0      CHECKING
   * 1 - 2  GOOD
   * 3      FAIR
   * 4 - 6  POOR
   */
  if (worstQuality === 0) {
    return { status: "CHECKING", uplink, downlink };
  }

  if (worstQuality <= 2) {
    return { status: "GOOD", uplink, downlink };
  }

  if (worstQuality === 3) {
    return { status: "FAIR", uplink, downlink };
  }

  return { status: "POOR", uplink, downlink };
};

export default function useAgora({
  appId,
  channelName,
  token,
  onCallEnd,
  showAlert,
  serverNow,
  hardEndsAt,

  /*
   * false means Privacy Mode.
   * Camera track must never be created.
   */
  startWithVideo = true,

  /*
   * Optional: async () => string (a fresh RTC token from your token server).
   * Used for token-privilege-will-expire. If omitted, the user is warned
   * instead of the token being renewed.
   */
  fetchFreshToken,
}) {
  const client = useRef(null);

  if (!client.current) {
    client.current = createClient({
      mode: "rtc",
      codec: VIDEO_CODEC,
    });
  }

  const localTracks = useRef({
    audioTrack: null,
    videoTrack: null,
  });

  const initialModeRef = useRef(null);

  if (!initialModeRef.current) {
    initialModeRef.current = resolveInitialMode();
  }

  const mountedRef = useRef(false);
  const joinedRef = useRef(false);
  const channelJoinedRef = useRef(false);
  const joiningRef = useRef(false);
  const joinOperationIdRef = useRef(0);
  const callEndNotifiedRef = useRef(false);
  const poorNetworkSampleCountRef = useRef(0);

  const onCallEndRef = useRef(onCallEnd);
  const showAlertRef = useRef(showAlert);
  const fetchFreshTokenRef = useRef(fetchFreshToken);

  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoStopped, setIsVideoStopped] = useState(false);
  const [optimizationMode, setOptimizationMode] = useState(
    initialModeRef.current.mode,
  );
  const [permissionHelper, setPermissionsHelper] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [networkQuality, setNetworkQuality] = useState(INITIAL_NETWORK_QUALITY);

  const [modeSource, setModeSource] = useState(initialModeRef.current.source);
  const [autoRung, setAutoRung] = useState(
    Math.max(0, AUTO_LADDER.indexOf(initialModeRef.current.mode)),
  );

  const [connectionState, setConnectionState] = useState("DISCONNECTED");
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [phoneFallbackSuggested, setPhoneFallbackSuggested] = useState(false);
  const [videoSuspendedByNetwork, setVideoSuspendedByNetwork] = useState(false);

  /*
   * Incremented whenever the local video track is created AFTER the initial
   * join (late recovery), so the UI can re-attach the self-view.
   */
  const [localVideoVersion, setLocalVideoVersion] = useState(0);

  /* Mirrors of state for use inside event listeners (no stale closures). */
  const optimizationModeRef = useRef(initialModeRef.current.mode);
  const sourceRef = useRef(initialModeRef.current.source);
  const rungRef = useRef(
    Math.max(0, AUTO_LADDER.indexOf(initialModeRef.current.mode)),
  );
  const isVideoStoppedRef = useRef(false);
  const videoSuspendedRef = useRef(false);

  /* Auto-ladder counters. */
  const badUplinkCountRef = useRef(0);
  const goodUplinkCountRef = useRef(0);
  const lastStepAtRef = useRef(0);
  const changeModeRef = useRef(null);

  const switchingRef = useRef(false);
  const pendingRef = useRef(null);

  const reconnectTimerRef = useRef(null);
  const videoRecoveryTimerRef = useRef(null);
  const videoRecoveryAttemptRef = useRef(0);
  const serverClockRef = useRef({ serverNow: null, receivedAt: 0 });

  useEffect(() => {
    onCallEndRef.current = onCallEnd;
  }, [onCallEnd]);

  useEffect(() => {
    showAlertRef.current = showAlert;
  }, [showAlert]);

  useEffect(() => {
    fetchFreshTokenRef.current = fetchFreshToken;
  }, [fetchFreshToken]);

  const updateRemoteUsers = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }

    setRemoteUsers(Array.from(client.current?.remoteUsers || []));
  }, []);

  /*
   * Receiver policy for one remote user's video:
   *   - fall back to the LOW stream (never to audio-only) under a poor link
   *   - in data-save, receive the low stream by choice
   * Both take the remote UID.
   */
  const applyRemoteVideoPolicy = useCallback(async (user) => {
    const rtcClient = client.current;

    if (!rtcClient || !user?.videoTrack) {
      return;
    }

    try {
      await rtcClient.setStreamFallbackOption(
        user.uid,
        STREAM_FALLBACK_LOW_STREAM,
      );
    } catch (fallbackError) {
      console.warn("Unable to set stream fallback option", fallbackError);
    }

    try {
      await rtcClient.setRemoteVideoStreamType(
        user.uid,
        optimizationModeRef.current === "data-save" ? 1 : 0,
      );
    } catch (streamTypeError) {
      console.warn("Unable to set remote stream type", streamTypeError);
    }
  }, []);

    /* Re-apply the current preset after the camera is switched back on. */
  const reapplyEncoderConfig = useCallback(async () => {
    const track = localTracks.current.videoTrack;
    const preset = QUALITY_PRESETS[optimizationModeRef.current];

    if (!track || !preset || track.enabled === false) {
      return;
    }

    try {
      await track.setEncoderConfiguration(preset);
    } catch (error) {
      console.warn("Unable to re-apply encoder configuration", error);
    }
  }, []);

  /*
   * After RECONNECTING -> CONNECTED: resubscribe remote media that went
   * missing and re-apply the receiver policy. Does NOT toggle the local
   * camera (stopping/starting capture on an unstable link caused errors).
   */
  const recoverMediaAfterReconnect = useCallback(async () => {
    const rtcClient = client.current;

    if (!rtcClient || !joinedRef.current) {
      return;
    }

    for (const user of Array.from(rtcClient.remoteUsers || [])) {
      try {
        if (user.hasVideo && !user.videoTrack) {
          await rtcClient.subscribe(user, "video");
        }

        if (user.hasAudio && !user.audioTrack) {
          await rtcClient.subscribe(user, "audio");
        }
      } catch (error) {
        console.warn("Resubscribe after reconnect failed", user.uid, error);
      }
    }

    updateRemoteUsers();

    for (const user of Array.from(rtcClient.remoteUsers || [])) {
      void applyRemoteVideoPolicy(user);
    }
  }, [applyRemoteVideoPolicy, updateRemoteUsers]);

  /*
   * Network-driven video suspension (audio-only floor).
   * Only used when AUDIO_ONLY_FLOOR_ENABLED is true (off by default).
   * Only ever resumes video that THIS code suspended, never one the
   * user turned off.
   */
  const suspendVideoForNetwork = useCallback(async () => {
    const videoTrack = localTracks.current.videoTrack;

    if (!videoTrack || videoSuspendedRef.current) {
      return false;
    }

    try {
      await videoTrack.setEnabled(false);
      videoSuspendedRef.current = true;

      if (mountedRef.current) {
        setVideoSuspendedByNetwork(true);
      }

      showAlertRef.current?.(
        "warning",
        "alert",
        "Weak connection: switched to audio only to keep the call stable.",
        "",
      );

      return true;
    } catch (error) {
      console.error("Unable to suspend video for network", error);
      return false;
    }
  }, []);

  const resumeNetworkSuspendedVideo = useCallback(async () => {
    if (!videoSuspendedRef.current) {
      return false;
    }

    const videoTrack = localTracks.current.videoTrack;

    videoSuspendedRef.current = false;

    if (mountedRef.current) {
      setVideoSuspendedByNetwork(false);
    }

    if (!videoTrack || isVideoStoppedRef.current) {
      return false;
    }

    try {
      await videoTrack.setEnabled(true);
      await reapplyEncoderConfig();

      showAlertRef.current?.(
        "success",
        "alert",
        "Connection improved: video resumed.",
        "",
      );

      return true;
    } catch (error) {
      console.error("Unable to resume video after network recovery", error);
      return false;
    }
  }, [reapplyEncoderConfig]);

  const disconnectChannel = useCallback(
    async ({ notifyCallEnd = false, reason = "left" } = {}) => {
      joinOperationIdRef.current += 1;
      joiningRef.current = false;

      const audioTrack = localTracks.current.audioTrack;
      const videoTrack = localTracks.current.videoTrack;

      localTracks.current = {
        audioTrack: null,
        videoTrack: null,
      };

      stopAndCloseTrack(audioTrack);
      stopAndCloseTrack(videoTrack);

      const shouldLeaveChannel = channelJoinedRef.current;

      channelJoinedRef.current = false;
      joinedRef.current = false;

      if (shouldLeaveChannel && client.current) {
        try {
          await client.current.leave();
        } catch (error) {
          console.error("Unable to leave Agora channel", error);
        }
      }

      poorNetworkSampleCountRef.current = 0;
      badUplinkCountRef.current = 0;
      goodUplinkCountRef.current = 0;
      lastStepAtRef.current = 0;
      videoSuspendedRef.current = false;
      isVideoStoppedRef.current = false;
      videoRecoveryAttemptRef.current = 0;

      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (videoRecoveryTimerRef.current) {
        window.clearTimeout(videoRecoveryTimerRef.current);
        videoRecoveryTimerRef.current = null;
      }

      if (mountedRef.current) {
        setRemoteUsers([]);
        setIsJoined(false);
        setIsJoining(false);
        setIsMuted(false);
        setIsVideoStopped(false);
        setNetworkQuality(INITIAL_NETWORK_QUALITY);
        setIsReconnecting(false);
        setPhoneFallbackSuggested(false);
        setVideoSuspendedByNetwork(false);
      }

      if (notifyCallEnd && !callEndNotifiedRef.current) {
        callEndNotifiedRef.current = true;

        onCallEndRef.current?.({ reason });
      }

      return true;
    },
    [],
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      void disconnectChannel({
        notifyCallEnd: false,
        reason: "unmounted",
      });
    };
  }, [disconnectChannel]);

  /*
   * Remote stream handling for data-save:
   *   data-save  -> receive the remote LOW stream (saves downlink data)
   *   otherwise  -> receive the HIGH stream
   * The "default" type also covers users who publish later.
   */
  const applyRemoteStreamType = useCallback(async (mode, users) => {
    const streamType = mode === "data-save" ? 1 : 0;

    try {
      await client.current?.setRemoteDefaultVideoStreamType?.(streamType);
    } catch (error) {
      console.warn("Unable to set default remote stream type", error);
    }

    for (const user of users || []) {
      if (!user?.videoTrack) continue;

      try {
        await client.current.setRemoteVideoStreamType(user.uid, streamType);
      } catch (error) {
        console.warn("Unable to set remote stream type", user.uid, error);
      }
    }
  }, []);

  useEffect(() => {
    if (!isJoined) {
      return;
    }

    void applyRemoteStreamType(
      optimizationMode,
      Array.from(client.current?.remoteUsers || []),
    );
  // }, [optimizationMode, isJoined, remoteUsers, applyRemoteStreamType]);
  }, [optimizationMode, isJoined, applyRemoteStreamType]);

  /* Lower the audio bitrate in data-save, restore otherwise. */
  useEffect(() => {
    const audioTrack = localTracks.current.audioTrack;

    if (
      !isJoined ||
      !audioTrack ||
      typeof audioTrack.setEncoderConfiguration !== "function"
    ) {
      return;
    }

    try {
      const result = audioTrack.setEncoderConfiguration(
        optimizationMode === "data-save"
          ? AUDIO_ENCODER_DATA_SAVER
          : AUDIO_ENCODER_DEFAULT,
      );

      if (result && typeof result.catch === "function") {
        result.catch((error) =>
          console.warn("Unable to update audio encoder", error),
        );
      }
    } catch (error) {
      console.warn("Unable to update audio encoder", error);
    }
  }, [optimizationMode, isJoined, localTracks]);

  /*
   * Event listeners are attached ONCE. Handlers read refs only.
   * If you add state-based dependencies to this effect, listeners will be
   * detached and re-attached on every change: keep new logic in refs.
   */
  useEffect(() => {
    const rtcClient = client.current;

    /*
     * Sender-side auto ladder.
     *
     * Uses UPLINK quality only: a poor downlink must not degrade what we
     * send (receiver-side stream fallback handles the downlink).
     * Reads refs only, so there are no stale closures and no double counting.
     */
    const runAutoLadder = (uplink) => {
      if (!joinedRef.current || sourceRef.current !== "auto") {
        return;
      }

      if (!Number.isInteger(uplink) || uplink <= 0) {
        return;
      }

      if (Date.now() - lastStepAtRef.current < STEP_COOLDOWN_MS) {
        return;
      }

      if (uplink >= STEP_DOWN_QUALITY) {
        badUplinkCountRef.current += 1;
        goodUplinkCountRef.current = 0;
      } else if (uplink <= 2) {
        goodUplinkCountRef.current += 1;
        badUplinkCountRef.current = 0;
      } else {
        badUplinkCountRef.current = 0;
        goodUplinkCountRef.current = 0;
      }

      const rung = rungRef.current;
      const lastRung = AUTO_LADDER.length - 1;

      // 1) Step down.
      if (badUplinkCountRef.current >= STEP_DOWN_SAMPLES && rung < lastRung) {
        badUplinkCountRef.current = 0;
        goodUplinkCountRef.current = 0;
        lastStepAtRef.current = Date.now();

        void changeModeRef.current?.(AUTO_LADDER[rung + 1], {
          source: "auto",
        });
        return;
      }

      // 2) Floor reached and still poor: audio-only (disabled by default).
      if (
        AUDIO_ONLY_FLOOR_ENABLED &&
        rung >= lastRung &&
        badUplinkCountRef.current >= AUDIO_ONLY_SAMPLES &&
        !videoSuspendedRef.current &&
        !isVideoStoppedRef.current &&
        localTracks.current.videoTrack
      ) {
        badUplinkCountRef.current = 0;
        goodUplinkCountRef.current = 0;
        lastStepAtRef.current = Date.now();

        void suspendVideoForNetwork();
        return;
      }

      // 3) Recovery: resume video first (if we suspended it), then step up.
      if (goodUplinkCountRef.current >= STEP_UP_SAMPLES) {
        goodUplinkCountRef.current = 0;
        badUplinkCountRef.current = 0;

        if (videoSuspendedRef.current) {
          lastStepAtRef.current = Date.now();
          void resumeNetworkSuspendedVideo();
          return;
        }

        if (rung > 0) {
          lastStepAtRef.current = Date.now();

          void changeModeRef.current?.(AUTO_LADDER[rung - 1], {
            source: "auto",
          });
        }
      }
    };

    const handleUserPublished = async (user, mediaType) => {
      try {
        await rtcClient.subscribe(user, mediaType);

        // Show the media immediately; the receiver policy is fine-tuning
        // and must never delay the picture.
        updateRemoteUsers();

        if (mediaType === "video") {
          void applyRemoteVideoPolicy(user);
        }
      } catch (error) {
        console.error("Unable to subscribe to remote Agora user", error);
      }
    };

    const handleUserUnpublished = () => {
      updateRemoteUsers();
    };

    const handleUserLeft = () => {
      updateRemoteUsers();
    };

    const handleNetworkQuality = (stats) => {
      if (!mountedRef.current) {
        return;
      }

      const nextQuality = resolveNetworkQuality(stats);

      if (nextQuality.status === "POOR") {
        poorNetworkSampleCountRef.current += 1;
      } else {
        /*
         * Require consecutive poor samples.
         * Any recovery resets the warning counter.
         */
        poorNetworkSampleCountRef.current = 0;
      }

      // Always a new object reference so the indicator re-renders.
      setNetworkQuality({
        ...nextQuality,
        poorPersistent:
          poorNetworkSampleCountRef.current >= POOR_NETWORK_SAMPLE_LIMIT,
      });

      runAutoLadder(Number(stats?.uplinkNetworkQuality));
    };

    const handleConnectionStateChange = (
      currentState,
      previousState,
      reason,
    ) => {
      if (!mountedRef.current) {
        return;
      }

      setConnectionState(currentState);

      if (currentState === "RECONNECTING") {
        setIsReconnecting(true);

        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;

            if (mountedRef.current) {
              setPhoneFallbackSuggested(true);
            }
          }, RECONNECT_PHONE_FALLBACK_MS);
        }
      } else {
        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }

        setIsReconnecting(false);
        setPhoneFallbackSuggested(false);

        if (currentState === "CONNECTED") {
          updateRemoteUsers();

          if (previousState === "RECONNECTING") {
            void recoverMediaAfterReconnect();
          }
        }
      }

      if (
        currentState === "DISCONNECTED" &&
        reason !== "LEAVE" &&
        joinedRef.current
      ) {
        setConnectionError(
          "The connection to the consultation was lost. Please retry.",
        );

        void disconnectChannel({
          notifyCallEnd: false,
          reason: "connection-lost",
        });
      }
    };

    const handleTokenWillExpire = async () => {
      const getToken = fetchFreshTokenRef.current;

      if (typeof getToken !== "function") {
        showAlertRef.current?.(
          "warning",
          "alert",
          "Your consultation access is about to expire.",
          "",
        );
        return;
      }

      try {
        const freshToken = await getToken();

        if (freshToken) {
          await rtcClient.renewToken(freshToken);
        }
      } catch (error) {
        console.error("Unable to renew RTC token", error);

        showAlertRef.current?.(
          "error",
          "alert",
          "Unable to extend consultation access.",
          "",
        );
      }
    };

    const handleTokenDidExpire = () => {
      if (!mountedRef.current) {
        return;
      }

      setConnectionError(
        "Your consultation access has expired. Please retry to rejoin.",
      );

      void disconnectChannel({
        notifyCallEnd: false,
        reason: "token-expired",
      });
    };

    rtcClient.on("user-published", handleUserPublished);
    rtcClient.on("user-unpublished", handleUserUnpublished);
    rtcClient.on("user-left", handleUserLeft);
    rtcClient.on("network-quality", handleNetworkQuality);
    rtcClient.on("connection-state-change", handleConnectionStateChange);
    rtcClient.on("token-privilege-will-expire", handleTokenWillExpire);
    rtcClient.on("token-privilege-did-expire", handleTokenDidExpire);

    return () => {
      rtcClient.off("user-published", handleUserPublished);
      rtcClient.off("user-unpublished", handleUserUnpublished);
      rtcClient.off("user-left", handleUserLeft);
      rtcClient.off("network-quality", handleNetworkQuality);
      rtcClient.off("connection-state-change", handleConnectionStateChange);
      rtcClient.off("token-privilege-will-expire", handleTokenWillExpire);
      rtcClient.off("token-privilege-did-expire", handleTokenDidExpire);

      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [
    updateRemoteUsers,
    disconnectChannel,
    suspendVideoForNetwork,
    resumeNetworkSuspendedVideo,
    applyRemoteVideoPolicy,
    recoverMediaAfterReconnect,
  ]);

  const joinChannel = useCallback(async () => {
    if (joiningRef.current || joinedRef.current) {
      return joinedRef.current;
    }

    if (!appId || !channelName) {
      const configurationError = new Error(
        "Video consultation configuration is unavailable.",
      );

      if (mountedRef.current) {
        setConnectionError(configurationError.message);
      }

      return false;
    }

    /* Pre-flight: unsupported browsers fail early with a clear message. */
    try {
      if (
        typeof AgoraRTC?.checkSystemRequirements === "function" &&
        !AgoraRTC.checkSystemRequirements()
      ) {
        if (mountedRef.current) {
          setConnectionError(
            "Your browser needs an update to support video consultations. Please update Chrome or Safari and try again.",
          );
        }

        return false;
      }
    } catch (preflightError) {
      console.warn("System requirements check failed to run", preflightError);
    }

    if (
      localTracks.current.audioTrack ||
      localTracks.current.videoTrack ||
      channelJoinedRef.current
    ) {
      await disconnectChannel({
        notifyCallEnd: false,
        reason: "reconnect",
      });
    }

    const operationId = joinOperationIdRef.current + 1;

    joinOperationIdRef.current = operationId;
    joiningRef.current = true;
    callEndNotifiedRef.current = false;

    if (mountedRef.current) {
      setIsJoining(true);
      setConnectionError(null);
      setCameraUnavailable(false);
      setPermissionsHelper(false);
    }

    const operationIsActive = () =>
      mountedRef.current && joinOperationIdRef.current === operationId;

    const startMode = optimizationModeRef.current;

    /*
     * Late camera recovery: after an audio-only join caused by a transient
     * camera/publish failure, retry a few times so video appears without the
     * user doing anything. Never retried for permission / no-camera errors.
     */
    const scheduleVideoRecovery = () => {
      const attempt = videoRecoveryAttemptRef.current;

      if (attempt >= VIDEO_RECOVERY_DELAYS_MS.length) {
        return;
      }

      videoRecoveryTimerRef.current = window.setTimeout(async () => {
        videoRecoveryTimerRef.current = null;

        if (
          !operationIsActive() ||
          !joinedRef.current ||
          localTracks.current.videoTrack
        ) {
          return;
        }

        videoRecoveryAttemptRef.current += 1;

        const result = await createVideoTrackWithFallback(
          buildCameraOptions(optimizationModeRef.current),
          operationIsActive,
        );

        if (result.aborted || !operationIsActive()) {
          return;
        }

        if (!result.track) {
          const kind = getMediaErrorKind(result.error);

          if (kind !== "permission" && kind !== "not-found") {
            scheduleVideoRecovery();
          }

          return;
        }

        try {
          try {
            client.current.setLowStreamParameter(LOW_STREAM_PARAMS);
          } catch (lowStreamError) {
            console.warn("Unable to set low stream parameters", lowStreamError);
          }

          await client.current.publish(result.track);

          if (!operationIsActive()) {
            stopAndCloseTrack(result.track);
            return;
          }

          localTracks.current.videoTrack = result.track;
          isVideoStoppedRef.current = false;

          setIsVideoStopped(false);
          setCameraUnavailable(false);
          setPermissionsHelper(false);
          setLocalVideoVersion((version) => version + 1);

          showAlertRef.current?.("success", "alert", "Camera connected.", "");
        } catch (publishError) {
          console.warn("Late video publish failed", publishError);
          stopAndCloseTrack(result.track);
          scheduleVideoRecovery();
        }
      }, VIDEO_RECOVERY_DELAYS_MS[attempt]);
    };

    try {
      const microphoneTrack = await createMicrophoneTrackWithConfig(startMode);

      if (!operationIsActive()) {
        stopAndCloseTrack(microphoneTrack);
        return false;
      }

      localTracks.current.audioTrack = microphoneTrack;

      let joinedWithVideo = false;
      let videoRecoverable = false;

      /*
       * =========================================================
       * CAMERA
       * =========================================================
       *
       * Privacy Mode:
       * startWithVideo === false
       *
       * In that case we do not call
       * createCameraVideoTrack() at all.
       */
      if (startWithVideo) {
        const result = await createVideoTrackWithFallback(
          buildCameraOptions(startMode),
          operationIsActive,
        );

        if (result.track) {
          localTracks.current.videoTrack = result.track;
          joinedWithVideo = true;
        } else if (result.aborted) {
          return false;
        } else if (mountedRef.current) {
          console.error(
            "Camera is unavailable; continuing with audio",
            result.error,
          );

          const kind = getMediaErrorKind(result.error);

          videoRecoverable =
            kind === "busy" || kind === "constraint" || kind === "unknown";

          setCameraUnavailable(true);
          setPermissionsHelper(isPermissionError(result.error));
        }
      }

      if (!operationIsActive()) {
        await disconnectChannel({
          notifyCallEnd: false,
          reason: "canceled",
        });
        return false;
      }

      /*
       * Dual-stream is enabled before join (Agora guide), for every video
       * call, so a camera that is recovered late still gets a low stream.
       * Best-effort only: failure must never block the join.
       */
      if (startWithVideo) {
        try {
          await client.current.enableDualStream();
        } catch (dualStreamError) {
          console.warn(
            "Dual-stream unavailable; continuing single-stream",
            dualStreamError,
          );
        }
      }

      try {
        await client.current.setRemoteDefaultVideoStreamType?.(
          startMode === "data-save" ? 1 : 0,
        );
      } catch (defaultStreamError) {
        console.warn("Unable to set default remote stream", defaultStreamError);
      }

      await client.current.join(appId, channelName, token || null, null);

      channelJoinedRef.current = true;

      if (!operationIsActive()) {
        await disconnectChannel({
          notifyCallEnd: false,
          reason: "canceled",
        });
        return false;
      }

      /* Low stream profile: after join, before publish. */
      if (startWithVideo) {
        try {
          client.current.setLowStreamParameter(LOW_STREAM_PARAMS);
        } catch (lowStreamError) {
          console.warn("Unable to set low stream parameters", lowStreamError);
        }
      }

      const publishableTracks = [
        localTracks.current.audioTrack,
        localTracks.current.videoTrack,
      ].filter(Boolean);

      if (!publishableTracks.length) {
        throw new Error("No local media tracks are available.");
      }

      /* Publish with exponential backoff, then degrade to audio-only. */
      let published = false;
      let lastPublishError = null;

      for (let attempt = 0; attempt < PUBLISH_MAX_ATTEMPTS; attempt += 1) {
        try {
          await client.current.publish(publishableTracks);
          published = true;
          break;
        } catch (publishError) {
          lastPublishError = publishError;
          console.warn(
            `Publish attempt ${attempt + 1} failed`,
            publishError?.message || publishError,
          );

          if (!operationIsActive()) {
            break;
          }

          if (attempt < PUBLISH_MAX_ATTEMPTS - 1) {
            await sleep(PUBLISH_RETRY_BASE_MS * 2 ** attempt);
          }
        }
      }

      if (
        !published &&
        operationIsActive() &&
        localTracks.current.videoTrack &&
        localTracks.current.audioTrack
      ) {
        try {
          await client.current.publish([localTracks.current.audioTrack]);
          published = true;

          stopAndCloseTrack(localTracks.current.videoTrack);
          localTracks.current.videoTrack = null;
          joinedWithVideo = false;
          videoRecoverable = true; // transient publish failure: try again later

          if (mountedRef.current) {
            setCameraUnavailable(true);
          }
        } catch (audioOnlyError) {
          lastPublishError = audioOnlyError;
        }
      }

      if (!published) {
        throw (
          lastPublishError || new Error("Unable to publish local media tracks.")
        );
      }

      if (!operationIsActive()) {
        await disconnectChannel({
          notifyCallEnd: false,
          reason: "canceled",
        });
        return false;
      }

      joinedRef.current = true;

      /* Apply the audio encoder if the mode changed while we were joining. */
      const joinedAudioTrack = localTracks.current.audioTrack;
      if (
        joinedAudioTrack &&
        optimizationModeRef.current !== startMode &&
        typeof joinedAudioTrack.setEncoderConfiguration === "function"
      ) {
        try {
          await joinedAudioTrack.setEncoderConfiguration(
            optimizationModeRef.current === "data-save"
              ? AUDIO_ENCODER_DATA_SAVER
              : AUDIO_ENCODER_DEFAULT,
          );
        } catch (audioConfigError) {
          console.warn("Unable to update audio encoder", audioConfigError);
        }
      }

      badUplinkCountRef.current = 0;
      goodUplinkCountRef.current = 0;
      lastStepAtRef.current = Date.now(); // let the first preset settle
      videoRecoveryAttemptRef.current = 0;

      if (mountedRef.current) {
        setIsJoined(true);
        setRemoteUsers(Array.from(client.current.remoteUsers || []));
      }

      showAlertRef.current?.(
        "success",
        "alert",
        joinedWithVideo
          ? "Joined consultation with audio and video."
          : "Joined consultation with audio.",
        "",
      );

      if (
        startWithVideo &&
        videoRecoverable &&
        !localTracks.current.videoTrack
      ) {
        scheduleVideoRecovery();
      }

      return true;
    } catch (error) {
      console.error("Failed to join Agora channel", error);

      if (operationIsActive()) {
        const message = isPermissionError(error)
          ? "Microphone access was denied. Allow microphone access in your browser settings and try again."
          : error?.message || "Unable to join the video consultation.";

        setConnectionError(message);

        if (isPermissionError(error)) {
          setPermissionsHelper(true);
        }
      }

      await disconnectChannel({
        notifyCallEnd: false,
        reason: "join-failed",
      });

      return false;
    } finally {
      joiningRef.current = false;

      if (mountedRef.current) {
        setIsJoining(false);
      }
    }
  }, [appId, channelName, token, disconnectChannel, startWithVideo]);

  const leaveChannel = useCallback(async () => {
    if (!joinedRef.current && !joiningRef.current) {
      return false;
    }

    let confirmed = true;

    if (typeof showAlertRef.current === "function") {
      confirmed = await showAlertRef.current(
        "error",
        "decision",
        "Leave this consultation? You can rejoin only while consultation access remains available.",
        "Leave",
        () => {},
      );
    }

    if (!confirmed) {
      return false;
    }

    await disconnectChannel({
      notifyCallEnd: true,
      reason: "patient-left",
    });

    showAlertRef.current?.(
      "success",
      "alert",
      "You left the consultation.",
      "",
    );

    return true;
  }, [disconnectChannel]);

  const toggleMute = useCallback(async () => {
    const audioTrack = localTracks.current.audioTrack;

    if (!audioTrack) {
      return false;
    }

    const nextMutedState = !isMuted;

    try {
      await audioTrack.setEnabled(!nextMutedState);
      setIsMuted(nextMutedState);
      return true;
    } catch (error) {
      console.error("Unable to change microphone state", error);

      showAlertRef.current?.(
        "error",
        "alert",
        "Unable to change microphone state.",
        "",
      );

      return false;
    }
  }, [isMuted]);

  const toggleVideo = useCallback(async () => {
    const videoTrack = localTracks.current.videoTrack;

    if (!videoTrack) {
      showAlertRef.current?.("warning", "alert", "Camera is unavailable.", "");
      return false;
    }

    /*
     * If the network ladder turned the camera off, the first tap
     * turns it back on rather than "stopping" an already-off camera.
     */
    if (videoSuspendedRef.current) {
      isVideoStoppedRef.current = false;
      setIsVideoStopped(false);
      return resumeNetworkSuspendedVideo();
    }

    const nextStoppedState = !isVideoStopped;

    try {
      await videoTrack.setEnabled(!nextStoppedState);
            if (!nextStoppedState) {
        await reapplyEncoderConfig();
      }
      isVideoStoppedRef.current = nextStoppedState;
      setIsVideoStopped(nextStoppedState);
      return true;
    } catch (error) {
      console.error("Unable to change camera state", error);

      showAlertRef.current?.(
        "error",
        "alert",
        "Unable to change camera state.",
        "",
      );

      return false;
    }
  }, [isVideoStopped, resumeNetworkSuspendedVideo, reapplyEncoderConfig]);

  const applyModeState = useCallback((mode, source, rung) => {
    optimizationModeRef.current = mode;
    sourceRef.current = source;
    rungRef.current = rung;

    setOptimizationMode(mode);
    setModeSource(source);
    setAutoRung(rung);
  }, []);

  /**
   * Reconfigure the local video track's encoder in place.
   * Never stops, closes, or re-creates the track.
   *
   * - "auto" as a mode forces source to "auto" so picking Auto
   *   from the picker resumes the ladder.
   * - Restores Agora's optimization hint so "detail" holds
   *   resolution and "motion" holds frame rate under congestion.
   * - Serialised via switchingRef / pendingRef so rapid taps
   *   coalesce to the last requested preset.
   * - A manual choice of data-save is remembered per device.
   */
  const changeOptimizationMode = useCallback(
    async (mode, { source = "manual" } = {}) => {
      const preset = QUALITY_PRESETS[mode];
      if (!preset) return false;

      const effectiveSource = mode === "auto" ? "auto" : source;
      const nextRung = Math.max(0, AUTO_LADDER.indexOf(mode));

      const videoTrack = localTracks.current.videoTrack;

      // Not joined / no video -> record intent, skip hardware.
      const cameraIsOff = Boolean(videoTrack) && videoTrack.enabled === false;

      // Not joined / no video / camera currently off -> record intent only.
      // The preset is applied when the camera is switched back on.
      if (!videoTrack || !joinedRef.current || cameraIsOff) {
        applyModeState(mode, effectiveSource, nextRung);

        if (source === "manual") {
          writeStoredManualMode(mode);
        }

        return true;
      }

      if (switchingRef.current) {
        // Manual picks always override a queued auto change.
        // Auto only queues if nothing else is pending.
        if (effectiveSource === "manual" || !pendingRef.current) {
          pendingRef.current = { mode, source: effectiveSource };
        }
        return true;
      }

      switchingRef.current = true;
      try {
        await videoTrack.setEncoderConfiguration(preset);

        // Restore Agora's stream-optimization hint. Without this, the
        // SDK falls back to its default degradation strategy under
        // congestion — which on most Web SDK versions is maintain-framerate
        // (drop resolution). That silently defeats "detail" mode.
        if (typeof videoTrack.setOptimizationMode === "function") {
          const optimizationHint =
            mode === "detail" || mode === "motion" ? mode : "balanced";
          try {
            await videoTrack.setOptimizationMode(optimizationHint);
          } catch (_) {
            /* Not fatal; encoder config above is still applied. */
          }
        }

        applyModeState(mode, effectiveSource, nextRung);

        if (source === "manual") {
          writeStoredManualMode(mode);
        }

        // A manual choice means the user wants video: undo any
        // network-driven audio-only suspension.
        if (effectiveSource === "manual") {
          void resumeNetworkSuspendedVideo();
        }

        return true;
      } catch (error) {
                if (error?.code === "TRACK_IS_DISABLED") {
          applyModeState(mode, effectiveSource, nextRung);
          return true;
        }
        
        console.error("Unable to update video quality", error);

        showAlertRef.current?.(
          "error",
          "alert",
          "Unable to update video quality.",
          "",
        );

        return false;
      } finally {
        switchingRef.current = false;
        const next = pendingRef.current;
        pendingRef.current = null;
        if (next && next.mode !== mode) {
          void changeModeRef.current?.(next.mode, { source: next.source });
        }
      }
    },
    [applyModeState, resumeNetworkSuspendedVideo],
  );

  useEffect(() => {
    changeModeRef.current = changeOptimizationMode;
  }, [changeOptimizationMode]);

  const revertToAuto = useCallback(
    () => changeOptimizationMode("auto", { source: "auto" }),
    [changeOptimizationMode],
  );

  const switchCamera = useCallback(async (...args) => {
    const videoTrack = localTracks.current.videoTrack;

    if (!videoTrack) {
      return false;
    }

    try {
      await switchAgoraCamera(videoTrack, ...args);
      return true;
    } catch (error) {
      console.error("Unable to switch camera", error);

      showAlertRef.current?.("error", "alert", "Unable to switch camera.", "");
      return false;
    }
  }, []);

  /*
   * Remember WHEN the server time was received, so the hard-end timer
   * measures from receipt instead of from whenever the effect runs.
   */
  useEffect(() => {
    serverClockRef.current = { serverNow, receivedAt: Date.now() };
  }, [serverNow]);

  /*
   * Client-side safety timer derived from the
   * server-provided clock and hard-end boundary.
   * The backend and RTC token must remain authoritative.
   */
  useEffect(() => {
    if (!isJoined || !serverNow || !hardEndsAt) {
      return undefined;
    }

    const serverNowMs = new Date(serverNow).getTime();
    const hardEndsAtMs = new Date(hardEndsAt).getTime();

    if (!Number.isFinite(serverNowMs) || !Number.isFinite(hardEndsAtMs)) {
      return undefined;
    }

    const disconnectAtHardEnd = () => {
      showAlertRef.current?.(
        "info",
        "alert",
        "The consultation time has ended.",
        "",
      );

      void disconnectChannel({
        notifyCallEnd: true,
        reason: "hard-ended",
      });
    };

    const receivedAt = serverClockRef.current.receivedAt || Date.now();
    const elapsedSinceReceipt = Date.now() - receivedAt;

    const delay =
      hardEndsAtMs - serverNowMs - elapsedSinceReceipt + HARD_END_PADDING_MS;

    if (delay <= 0) {
      disconnectAtHardEnd();
      return undefined;
    }

    const timerId = window.setTimeout(disconnectAtHardEnd, delay);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isJoined, serverNow, hardEndsAt, disconnectChannel]);

  /*
   * Field diagnostics (off unless localStorage "deedmed.debugRtc" === "1").
   * Logs what is actually captured/sent/received every 5 s so support can
   * tell encoder, network and fallback problems apart.
   */
  useEffect(() => {
    if (!isRtcDebugEnabled()) {
      return undefined;
    }

    const id = window.setInterval(() => {
      const rtcClient = client.current;

      if (!rtcClient || !joinedRef.current) {
        return;
      }

      try {
        const remoteStats = rtcClient.getRemoteVideoStats?.() || {};
        const localStats = rtcClient.getLocalVideoStats?.() || {};
        const first = Object.values(remoteStats)[0];
        const captured = localTracks.current.videoTrack
          ?.getMediaStreamTrack?.()
          ?.getSettings?.();

        console.log("[RTC debug]", {
          mode: optimizationModeRef.current,
          source: sourceRef.current,
          rung: rungRef.current,
          captured: captured
            ? {
                w: captured.width,
                h: captured.height,
                fps: captured.frameRate,
              }
            : null,
          sent: {
            w: localStats.sendResolutionWidth,
            h: localStats.sendResolutionHeight,
            fps: localStats.sendFrameRate,
            bitrate: localStats.sendBitrate,
          },
          received: first
            ? {
                w: first.receiveResolutionWidth,
                h: first.receiveResolutionHeight,
                fps: first.receiveFrameRate,
                bitrate: first.receiveBitrate,
                loss: first.packetLossRate,
              }
            : null,
        });
      } catch (error) {
        console.warn("[RTC debug] stats unavailable", error);
      }
    }, 5000);

    return () => window.clearInterval(id);
  }, []);

  return {
    joinChannel,
    leaveChannel,
    disconnectChannel,
    toggleMute,
    toggleVideo,
    changeOptimizationMode,
    revertToAuto,
    switchCamera,

    isJoined,
    isJoining,
    isMuted,
    isVideoStopped,
    optimizationMode,
    modeSource,
    autoRung,
    remoteUsers,
    localTracks,
    localVideoVersion,

    cameraUnavailable,
    permissionHelper,
    setPermissionsHelper,
    connectionError,

    networkQuality,

    connectionState,
    isReconnecting,
    phoneFallbackSuggested,
    videoSuspendedByNetwork,
  };
}